import React, { useState, useEffect } from 'react';
import { 
  Star, Plus, Edit, Trash2, Eye, EyeOff, Upload, Play, Pause, 
  ChevronLeft, ChevronRight, Settings, Image as ImageIcon,
  Video, Layout, Type, Palette, Clock, ArrowRight, Save, X
} from 'lucide-react';
import { ecommerceService } from '../../services/ecommerceService';
import { FileUpload } from '../common/FileUpload';
import toast from 'react-hot-toast';

export const HeroManager = ({ storeId, bookData, onUpdate }) => {
  const [heroSettings, setHeroSettings] = useState({
    enabled: true,
    type: 'single',
    slides: [],
    autoplay: true,
    slideDuration: 5000,
    showTrustIndicators: true,
    trustIndicators: []
  });

  // Separate state for global settings form
  const [globalSettingsForm, setGlobalSettingsForm] = useState({});
  const [globalSettingsChanged, setGlobalSettingsChanged] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [savingGlobalSettings, setSavingGlobalSettings] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  useEffect(() => {
    loadHeroSettings();
  }, [storeId]);

  // Initialize global settings form when hero settings load
  useEffect(() => {
    if (heroSettings && Object.keys(heroSettings).length > 0) {
      setGlobalSettingsForm({
        type: heroSettings.heroType || heroSettings.type || 'single',
        autoplay: heroSettings.autoplay !== false,
        slideDuration: heroSettings.slideDuration || 5000,
        showTrustIndicators: heroSettings.showTrustIndicators !== false,
        title: heroSettings.title || '',
        subtitle: heroSettings.subtitle || '',
        description: heroSettings.description || '',
        ctaText: heroSettings.ctaText || 'Shop Now',
        ctaSecondaryText: heroSettings.ctaSecondaryText || 'Learn More',
        textColor: heroSettings.textColor || '#ffffff',
        overlayOpacity: heroSettings.overlayOpacity || 0.4,
        textPosition: heroSettings.textPosition || 'center'
      });
      setGlobalSettingsChanged(false);
    }
  }, [heroSettings]);

  const loadHeroSettings = async () => {
    setLoading(true);
    try {
      const settings = await ecommerceService.getHeroSettings(storeId);
      if (settings) {
        // Ensure trust indicators have unique IDs
        const trustIndicators = (settings.trustIndicators || settings.features || []).map((indicator, index) => ({
          ...indicator,
          id: indicator.id || `indicator-${Date.now()}-${index}` // Ensure each has an ID
        }));
        
        setHeroSettings({
          ...settings,
          trustIndicators,
          features: trustIndicators // Keep both for compatibility
        });
      }
    } catch (error) {
      console.error('Error loading hero settings:', error);
    } finally {
      setLoading(false);
    }
  };
  // Handle global settings form changes
  const handleGlobalSettingChange = (field, value) => {
    setGlobalSettingsForm(prev => ({
      ...prev,
      [field]: value
    }));
    setGlobalSettingsChanged(true);
  };

  // Save global settings
  const saveGlobalSettings = async () => {
    setSavingGlobalSettings(true);
    try {
      const updatedSettings = {
        ...heroSettings,
        // Core type fields
        type: 'hero', 
        heroType: globalSettingsForm.type, 
        
        // Content fields - these map directly to EcommerceHeroSection props
        title: globalSettingsForm.title,
        subtitle: globalSettingsForm.subtitle,
        description: globalSettingsForm.description,
        ctaText: globalSettingsForm.ctaText,
        ctaSecondaryText: globalSettingsForm.ctaSecondaryText,
        
        // Visual styling fields
        backgroundImage: globalSettingsForm.backgroundImage || '',
        backgroundVideo: globalSettingsForm.backgroundVideo || '',
        textColor: globalSettingsForm.textColor,
        overlayOpacity: globalSettingsForm.overlayOpacity,
        textPosition: globalSettingsForm.textPosition,
        
        // Behavior settings
        autoplay: globalSettingsForm.autoplay,
        slideDuration: globalSettingsForm.slideDuration,
        
        // Trust indicators
        showTrustIndicators: globalSettingsForm.showTrustIndicators,
        trustIndicators: heroSettings.trustIndicators || [], // Keep existing trust indicators
        features: heroSettings.trustIndicators || [], // Alias for compatibility
        
        // Slides array - sync first slide with global settings
        slides: heroSettings.slides.length > 0 ? heroSettings.slides.map((slide, index) => 
          index === 0 ? {
            ...slide,
            title: globalSettingsForm.title,
            subtitle: globalSettingsForm.subtitle,
            description: globalSettingsForm.description,
            ctaText: globalSettingsForm.ctaText,
            ctaSecondaryText: globalSettingsForm.ctaSecondaryText,
            backgroundImage: globalSettingsForm.backgroundImage || slide.backgroundImage,
            backgroundVideo: globalSettingsForm.backgroundVideo || slide.backgroundVideo,
            textColor: globalSettingsForm.textColor,
            overlayOpacity: globalSettingsForm.overlayOpacity,
            textPosition: globalSettingsForm.textPosition
          } : slide
        ) : [{
          id: 1,
          title: globalSettingsForm.title,
          subtitle: globalSettingsForm.subtitle,
          description: globalSettingsForm.description,
          ctaText: globalSettingsForm.ctaText,
          ctaSecondaryText: globalSettingsForm.ctaSecondaryText,
          backgroundImage: globalSettingsForm.backgroundImage || '',
          backgroundVideo: globalSettingsForm.backgroundVideo || '',
          textColor: globalSettingsForm.textColor,
          overlayOpacity: globalSettingsForm.overlayOpacity,
          textPosition: globalSettingsForm.textPosition
        }]
      };

      await ecommerceService.updateHeroSettings(storeId, updatedSettings);
      setHeroSettings(updatedSettings);
      setGlobalSettingsChanged(false);
      onUpdate?.();
      
    } catch (error) {
      console.error('Error saving global settings:', error);
    } finally {
      setSavingGlobalSettings(false);
    }
  };


  // Reset global settings form
  const resetGlobalSettings = () => {
    setGlobalSettingsForm({
      type: heroSettings.heroType || heroSettings.type || 'single',
      autoplay: heroSettings.autoplay !== false,
      slideDuration: heroSettings.slideDuration || 5000,
      showTrustIndicators: heroSettings.showTrustIndicators !== false,
      title: heroSettings.title || '',
      subtitle: heroSettings.subtitle || '',
      description: heroSettings.description || '',
      ctaText: heroSettings.ctaText || 'Shop Now',
      ctaSecondaryText: heroSettings.ctaSecondaryText || 'Learn More',
      textColor: heroSettings.textColor || '#ffffff',
      overlayOpacity: heroSettings.overlayOpacity || 0.4,
      textPosition: heroSettings.textPosition || 'center'
    });
    setGlobalSettingsChanged(false);
  };

  const saveHeroSettings = async (settings = heroSettings) => {
    try {
      await ecommerceService.updateHeroSettings(storeId, settings);
      setHeroSettings(settings);
      onUpdate?.({ hero: settings });
      toast.success('Hero section updated successfully');
    } catch (error) {
      console.error('Error saving hero settings:', error);
      toast.error('Failed to update hero section');
    }
  };

  const handleSlideUpdate = async (slideId, updates) => {
    try {
      // Build fully safe slides array
      const updatedSlides = heroSettings.slides.map(slide => {
        if (slide.id !== slideId) return slide;

        return {
          ...slide,

          // Use ?? instead of || so empty strings ("") remain valid
          title: updates.title ?? slide.title ?? '',
          subtitle: updates.subtitle ?? slide.subtitle ?? '',
          description: updates.description ?? slide.description ?? '',
          ctaText: updates.ctaText ?? slide.ctaText ?? '',
          ctaSecondaryText: updates.ctaSecondaryText ?? slide.ctaSecondaryText ?? '',

          backgroundImage: updates.backgroundImage ?? slide.backgroundImage ?? '',
          backgroundVideo: updates.backgroundVideo ?? slide.backgroundVideo ?? '',

          textColor: updates.textColor ?? slide.textColor ?? '#ffffff',
          overlayOpacity: updates.overlayOpacity ?? slide.overlayOpacity ?? 0.4,
          textPosition: updates.textPosition ?? slide.textPosition ?? 'center',

          active: updates.active ?? slide.active ?? true,
          order: slide.order ?? 0
        };
      });

      // Build new settings object safely
      const newSettings = {
        ...heroSettings,
        slides: updatedSlides,

        // If first slide is updated → sync global hero settings
        ...(slideId === updatedSlides[0]?.id
          ? {
              title: updates.title ?? heroSettings.title ?? '',
              subtitle: updates.subtitle ?? heroSettings.subtitle ?? '',
              description: updates.description ?? heroSettings.description ?? '',
              ctaText: updates.ctaText ?? heroSettings.ctaText ?? '',
              ctaSecondaryText: updates.ctaSecondaryText ?? heroSettings.ctaSecondaryText ?? '',

              backgroundImage:
                updates.backgroundImage ??
                heroSettings.backgroundImage ??
                updatedSlides[0].backgroundImage ??
                '',

              backgroundVideo:
                updates.backgroundVideo ??
                heroSettings.backgroundVideo ??
                updatedSlides[0].backgroundVideo ??
                '',

              textColor: updates.textColor ?? heroSettings.textColor ?? '#ffffff',
              overlayOpacity:
                updates.overlayOpacity ?? heroSettings.overlayOpacity ?? 0.4,
              textPosition:
                updates.textPosition ?? heroSettings.textPosition ?? 'center'
            }
          : {})
      };

      // Update state
      setHeroSettings(newSettings);

      // Save to Firestore
      await ecommerceService.updateHeroSettings(storeId, newSettings);

      onUpdate?.();
    } catch (error) {
      console.error('Error updating slide:', error);
      toast.error('Failed to update slide');
    }
  };


  const addNewSlide = () => {
    const newSlide = {
      id: `slide-${Date.now()}`,
      title: 'New Slide Title',
      subtitle: 'Slide subtitle',
      description: 'Slide description',
      ctaText: 'Shop Now',
      ctaSecondaryText: '',
      backgroundImage: '',
      backgroundVideo: '',
      textColor: '#ffffff',
      overlayOpacity: 0.4,
      textPosition: 'left',
      active: true
    };
    
    const newSettings = {
      ...heroSettings,
      slides: [...heroSettings.slides, newSlide]
    };
    setHeroSettings(newSettings);
    saveHeroSettings(newSettings);
    setEditingSlide(newSlide.id);
  };

  const deleteSlide = (slideId) => {
    if (heroSettings.slides.length <= 1) {
      toast.error('Cannot delete the last slide');
      return;
    }
    
    const newSettings = {
      ...heroSettings,
      slides: heroSettings.slides.filter(slide => slide.id !== slideId)
    };
    setHeroSettings(newSettings);
    saveHeroSettings(newSettings);
  };

  const handleImageUpload = async (slideId, file) => {
    try {
        const { firestoreService } = await import('../../services/firestoreService');
        const uploadResult = await firestoreService.uploadFile(file, `pb-${storeId}/hero/images`, {
        maxSize: 10 * 1024 * 1024, // 10MB
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
        });
        handleSlideUpdate(slideId, { backgroundImage: uploadResult.url });
        toast.success('Image uploaded successfully');
    } catch (error) {
        console.error('Error uploading image:', error);
        toast.error('Failed to upload image');
    }
  };

  const handleVideoUpload = async (slideId, file) => {
    try {
        const { firestoreService } = await import('../../services/firestoreService');
        const uploadResult = await firestoreService.uploadFile(file, `pb-${storeId}/sections/hero/videos`, {
        maxSize: 50 * 1024 * 1024, // 50MB
        allowedTypes: ['video/mp4', 'video/webm', 'video/ogg']
        });
        handleSlideUpdate(slideId, { backgroundVideo: uploadResult.url });
        toast.success('Video uploaded successfully');
    } catch (error) {
        console.error('Error uploading video:', error);
        toast.error('Failed to upload video');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Star className="h-6 w-6 text-yellow-500" />
            Hero Section Manager
          </h2>
          <p className="text-gray-600">Configure your store's main banner area</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
              previewMode 
                ? 'bg-blue-600 text-white' 
                : 'bg-white text-gray-700 border'
            }`}
          >
            <Eye className="h-4 w-4" />
            {previewMode ? 'Exit Preview' : 'Preview'}
          </button>
        </div>
      </div>

      {/* Global Settings with Save Functionality */}
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Global Settings
          </h3>
          
          {/* Save/Reset Buttons */}
          {globalSettingsChanged && (
            <div className="flex items-center gap-2">
              <button
                onClick={resetGlobalSettings}
                className="px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Reset
              </button>
              <button
                onClick={saveGlobalSettings}
                disabled={savingGlobalSettings}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {savingGlobalSettings ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
        
        {/* Settings Form */}
        <div className="space-y-6">
          {/* Hero Type and Basic Settings */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Hero Type</label>
              <select
                value={globalSettingsForm.type}
                onChange={(e) => handleGlobalSettingChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="single">Single Banner</option>
                <option value="carousel">Image Carousel</option>
                <option value="video">Video Background</option>
                <option value="split">Split Layout</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Slide Duration (ms)</label>
              <input
                type="number"
                value={globalSettingsForm.slideDuration}
                onChange={(e) => handleGlobalSettingChange('slideDuration', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                min="1000"
                step="500"
              />
            </div>

            <div className="flex items-center">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={globalSettingsForm.autoplay}
                  onChange={(e) => handleGlobalSettingChange('autoplay', e.target.checked)}
                  className="sr-only"
                />
                <div className={`relative inline-block w-10 h-6 transition duration-200 ease-linear rounded-full ${
                  globalSettingsForm.autoplay ? 'bg-blue-500' : 'bg-gray-300'
                }`}>
                  <div className={`absolute left-0 top-0 bg-white border-2 border-gray-300 rounded-full h-6 w-6 transition transform duration-100 ease-linear ${
                    globalSettingsForm.autoplay ? 'translate-x-full border-blue-500' : 'translate-x-0'
                  }`}></div>
                </div>
                <span className="ml-3 text-sm font-medium text-gray-700">Autoplay Slides</span>
              </label>
            </div>
          </div>

          {/* Content Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Title</label>
              <input
                type="text"
                value={globalSettingsForm.title}
                onChange={(e) => handleGlobalSettingChange('title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Welcome to Our Store"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default Subtitle</label>
              <input
                type="text"
                value={globalSettingsForm.subtitle}
                onChange={(e) => handleGlobalSettingChange('subtitle', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Discover amazing products at unbeatable prices"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Default Description</label>
            <textarea
              value={globalSettingsForm.description}
              onChange={(e) => handleGlobalSettingChange('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder="Shop the latest trends and find everything you need in one place."
            />
          </div>

          {/* CTA Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Primary CTA Text</label>
              <input
                type="text"
                value={globalSettingsForm.ctaText}
                onChange={(e) => handleGlobalSettingChange('ctaText', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Shop Now"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Secondary CTA Text</label>
              <input
                type="text"
                value={globalSettingsForm.ctaSecondaryText}
                onChange={(e) => handleGlobalSettingChange('ctaSecondaryText', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Learn More"
              />
            </div>
          </div>

          {/* Styling Settings */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Text Position</label>
              <select
                value={globalSettingsForm.textPosition}
                onChange={(e) => handleGlobalSettingChange('textPosition', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
              <input
                type="color"
                value={globalSettingsForm.textColor}
                onChange={(e) => handleGlobalSettingChange('textColor', e.target.value)}
                className="w-full h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Overlay Opacity: {Math.round(globalSettingsForm.overlayOpacity * 100)}%
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={globalSettingsForm.overlayOpacity}
                onChange={(e) => handleGlobalSettingChange('overlayOpacity', parseFloat(e.target.value))}
                className="w-full"
              />
            </div>
          </div>

          {/* Trust Indicators Toggle */}
          <div>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={globalSettingsForm.showTrustIndicators}
                onChange={(e) => handleGlobalSettingChange('showTrustIndicators', e.target.checked)}
                className="sr-only"
              />
              <div className={`relative inline-block w-10 h-6 transition duration-200 ease-linear rounded-full ${
                globalSettingsForm.showTrustIndicators ? 'bg-blue-500' : 'bg-gray-300'
              }`}>
                <div className={`absolute left-0 top-0 bg-white border-2 border-gray-300 rounded-full h-6 w-6 transition transform duration-100 ease-linear ${
                  globalSettingsForm.showTrustIndicators ? 'translate-x-full border-blue-500' : 'translate-x-0'
                }`}></div>
              </div>
              <span className="ml-3 text-sm font-medium text-gray-700">Show Trust Indicators</span>
            </label>
          </div>
        </div>

        {/* Unsaved Changes Indicator */}
        {globalSettingsChanged && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2 text-yellow-800">
              <Clock className="h-4 w-4" />
              <span className="text-sm font-medium">You have unsaved changes</span>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
          <Star className="h-5 w-5" />
          Trust Indicators
        </h3>
        
        <TrustIndicatorsManager 
          indicators={heroSettings.trustIndicators || []}
          onChange={(updatedIndicators) => {
            const newSettings = {
              ...heroSettings,
              trustIndicators: updatedIndicators,
              features: updatedIndicators // Alias for compatibility with EcommerceHeroSection
            };
            setHeroSettings(newSettings);
            ecommerceService.updateHeroSettings(storeId, newSettings);
            toast.success('Trust indicators updated');
          }}
        />
      </div>

      {/* Individual Slides Management */}
      <div className="bg-white border rounded-lg">
        <div className="p-6 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Layout className="h-5 w-5" />
            Individual Slides ({heroSettings.slides?.length || 0})
          </h3>
          <button
            onClick={() => {
              // Add new slide functionality
              const newSlide = {
                id: `slide-${Date.now()}`,
                title: '',
                subtitle: '',
                description: '',
                ctaText: globalSettingsForm.ctaText || 'Shop Now',
                ctaSecondaryText: globalSettingsForm.ctaSecondaryText || '',
                backgroundImage: '',
                backgroundVideo: '',
                textColor: globalSettingsForm.textColor || '#ffffff',
                overlayOpacity: globalSettingsForm.overlayOpacity || 0.4,
                textPosition: globalSettingsForm.textPosition || 'center',
                active: true,
                order: heroSettings.slides?.length || 0
              };
              
              const newSettings = {
                ...heroSettings,
                slides: [...(heroSettings.slides || []), newSlide]
              };
              setHeroSettings(newSettings);
              ecommerceService.updateHeroSettings(storeId, newSettings);
              setEditingSlide(newSlide.id);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Slide
          </button>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {heroSettings.slides.map((slide, index) => (
              <div key={slide.id} className="border rounded-lg overflow-hidden">
                {/* Slide Preview */}
                <div 
                  className="h-32 bg-gray-200 relative bg-cover bg-center"
                  style={{ 
                    backgroundImage: slide.backgroundImage ? `url(${slide.backgroundImage})` : 'none',
                    backgroundColor: slide.backgroundImage ? 'transparent' : '#f3f4f6'
                  }}
                >
                  {!slide.backgroundImage && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <ImageIcon className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setEditingSlide(slide.id)}
                      className="p-2 bg-white rounded-full text-gray-700 hover:text-blue-600"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                {/* Slide Info */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium truncate">{slide.title}</h4>
                    <span className="text-xs text-gray-500">#{index + 1}</span>
                  </div>
                  <p className="text-sm text-gray-600 truncate mb-3">{slide.subtitle}</p>
                  
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => {
                        const newSlides = [...heroSettings.slides];
                        newSlides[index] = { ...slide, active: !slide.active };
                        const newSettings = { ...heroSettings, slides: newSlides };
                        setHeroSettings(newSettings);
                        saveHeroSettings(newSettings);
                      }}
                      className={`p-1 rounded ${slide.active ? 'text-green-600' : 'text-gray-400'}`}
                    >
                      {slide.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </button>
                    
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setEditingSlide(slide.id)}
                        className="p-1 text-gray-600 hover:text-blue-600"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {heroSettings.slides.length > 1 && (
                        <button
                          onClick={() => deleteSlide(slide.id)}
                          className="p-1 text-gray-600 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Slide Editor Modal */}
      {editingSlide && (
        <SlideEditor
          slide={heroSettings.slides?.find(s => s.id === editingSlide)}
          onSave={(updates) => {
            const updatedSlides = heroSettings.slides.map(slide =>
              slide.id === editingSlide ? { ...slide, ...updates } : slide
            );
            const newSettings = { ...heroSettings, slides: updatedSlides };
            setHeroSettings(newSettings);
            ecommerceService.updateHeroSettings(storeId, newSettings);
          }}
          onClose={() => setEditingSlide(null)}
          onImageUpload={(file) => handleImageUpload(editingSlide, file)}
        />
      )}
    </div>
  );
};

const TrustIndicatorsManager = ({ indicators = [], onChange }) => {
  const [availableIcons] = useState([
    { value: 'Truck', label: 'Truck / Shipping' },
    { value: 'Shield', label: 'Shield / Security' },
    { value: 'Award', label: 'Award / Quality' },
    { value: 'Users', label: 'Users / Community' },
    { value: 'TrendingUp', label: 'Trending / Growth' },
    { value: 'Gift', label: 'Gift / Rewards' },
    { value: 'Percent', label: 'Percent / Discount' },
    { value: 'Tag', label: 'Tag / Deals' },
    { value: 'Zap', label: 'Zap / Fast' },
    { value: 'Clock', label: 'Clock / 24/7' },
    { value: 'Phone', label: 'Phone / Support' },
    { value: 'Mail', label: 'Mail / Contact' },
    { value: 'MapPin', label: 'Location / Global' }
  ]);

  const addIndicator = () => {
    const newIndicator = {
      id: `indicator-${Date.now()}`, // Unique ID
      icon: 'Truck',
      text: 'New Feature'
    };
    onChange([...indicators, newIndicator]);
  };

  const updateIndicator = (id, field, value) => {
    const updated = indicators.map(ind => {
      // Only update the indicator with matching ID
      if (ind.id === id) {
        return { ...ind, [field]: value };
      }
      return ind;
    });
    onChange(updated);
  };

  const removeIndicator = (id) => {
    onChange(indicators.filter(ind => ind.id !== id));
  };

  return (
    <div className="space-y-4">
      {indicators.length === 0 && (
        <p className="text-gray-500 text-sm">No trust indicators added yet.</p>
      )}
      
      {indicators.map((indicator) => {
        // Ensure each indicator has a unique ID
        const indicatorId = indicator.id || `indicator-${Math.random()}`;
        
        return (
          <div key={indicatorId} className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
            {/* Icon Select */}
            <select
              value={indicator.icon || 'Truck'}
              onChange={(e) => updateIndicator(indicatorId, 'icon', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white min-w-[180px]"
            >
              {availableIcons.map(icon => (
                <option key={icon.value} value={icon.value}>
                  {icon.label}
                </option>
              ))}
            </select>
            
            {/* Text Input */}
            <input
              type="text"
              value={indicator.text || ''}
              onChange={(e) => updateIndicator(indicatorId, 'text', e.target.value)}
              placeholder="Feature text"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            
            {/* Delete Button */}
            <button
              onClick={() => removeIndicator(indicatorId)}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Remove indicator"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        );
      })}
      
      {/* Add Button */}
      <button
        onClick={addIndicator}
        className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 flex items-center justify-center gap-2 transition-all"
      >
        <Plus className="h-4 w-4" />
        Add Trust Indicator
      </button>
    </div>
  );
};

// Slide Editor Modal Component
const SlideEditor = ({ slide, onSave, onClose, onImageUpload }) => {
  const [formData, setFormData] = useState(slide);

  const handleInputChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    onSave(updated);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold">Edit Slide</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Content Fields */}
          <div>
            <h4 className="text-md font-semibold text-gray-900 mb-4">Slide Content</h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Slide title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => handleInputChange('subtitle', e.target.value)}
                  placeholder="Slide subtitle"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Slide description"
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Primary CTA Text</label>
                  <input
                    type="text"
                    value={formData.ctaText}
                    onChange={(e) => handleInputChange('ctaText', e.target.value)}
                    placeholder="Shop Now"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Secondary CTA Text</label>
                  <input
                    type="text"
                    value={formData.ctaSecondaryText}
                    onChange={(e) => handleInputChange('ctaSecondaryText', e.target.value)}
                    placeholder="Learn More"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Background Media */}
          <div className="border-t pt-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4">Background Media</h4>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Background Image URL</label>
                <input
                  type="text"
                  value={formData.backgroundImage || ''}
                  onChange={(e) => handleInputChange('backgroundImage', e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload Background Image</label>
                <FileUpload
                  onFileSelect={(file) => onImageUpload(file)}
                  accept="image/*"
                  maxSize={5}
                  className="w-full"
                >
                  <div className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 cursor-pointer">
                    <ImageIcon className="h-5 w-5 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {formData.backgroundImage ? 'Change Image' : 'Upload Image'}
                    </span>
                  </div>
                </FileUpload>
                
                {formData.backgroundImage && (
                  <div className="mt-2 relative">
                    <img 
                      src={formData.backgroundImage} 
                      alt="Preview" 
                      className="w-full h-32 object-cover rounded-lg"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Background Video URL (Optional)</label>
                <input
                  type="text"
                  value={formData.backgroundVideo || ''}
                  onChange={(e) => handleInputChange('backgroundVideo', e.target.value)}
                  placeholder="https://example.com/video.mp4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Visual Styling */}
          <div className="border-t pt-6">
            <h4 className="text-md font-semibold text-gray-900 mb-4">Visual Styling</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Text Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.textColor}
                    onChange={(e) => handleInputChange('textColor', e.target.value)}
                    className="h-10 w-16 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.textColor}
                    onChange={(e) => handleInputChange('textColor', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Text Position</label>
                <select
                  value={formData.textPosition}
                  onChange={(e) => handleInputChange('textPosition', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Overlay Opacity: {Math.round(formData.overlayOpacity * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={formData.overlayOpacity}
                  onChange={(e) => handleInputChange('overlayOpacity', parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t bg-gray-50 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};